import 'reflect-metadata';

import {isSignal} from '@angular/core';

export namespace BE {
	const BE_PROP_KEY = Symbol('BEPropKey');

	interface IBEPropConfig {
		keyOverride?: string;
		transform?: (value: any) => any;
	}

	interface IBEOptionalConfig {
		keyOverride?: string;
	}

	interface IBEPropMetadata extends IBEPropConfig {
		required?: boolean;
	}
	/**
	 * BEProp is a decorator factory that returns a PropertyDecorator.
	 * The returned decorator will attach metadata to a property of a class.
	 *
	 * @param {IBEPropConfig} props - An optional configuration object for the property.
	 * @param {string} props.keyOverride - If provided, this will be used as the key in the serialized object instead of the property name.
	 * @param {Function} props.transform - If provided, this function will be used to transform the property value during serialization.
	 *
	 * @returns {PropertyDecorator} - A decorator function that can be used to decorate a class property.
	 *
	 * @example
	 * class MyClass {
	 *   @BEProp({ keyOverride: 'myProp', transform: value => `Hello, ${value}!` })
	 *   prop: string;
	 * }
	 *
	 * const myInstance = new MyClass();
	 * myInstance.prop = 'World';
	 * console.log(JSON.stringify(myInstance)); // Outputs: { "myProp": "Hello, World!" }
	 */
	export function BEProp(props?: IBEPropConfig): PropertyDecorator {
		return (target: any, propertyKey: string | symbol) => {
			const metadata: IBEPropMetadata = {
				required: true,
				...props,
			};
			Reflect.defineMetadata(BE_PROP_KEY, metadata, target, propertyKey);
		};
	}

	/**
	 * BEOptional is a decorator factory that returns a PropertyDecorator.
	 * The returned decorator will attach metadata to a property of a class, marking it as optional.
	 * This means that if the property value is undefined during serialization, no error will be thrown.
	 *
	 * @returns {PropertyDecorator} - A decorator function that can be used to decorate a class property.
	 *
	 * @example
	 * class MyClass {
	 *   @BEOptional()
	 *   optionalProp: string;
	 * }
	 *
	 * const myInstance = new MyClass();
	 * console.log(JSON.stringify(myInstance)); // Outputs: {}
	 */
	export function BEOptional(props?: IBEOptionalConfig): PropertyDecorator {
		return (target: any, propertyKey: string | symbol) => {
			const metadata: IBEPropMetadata = {
				required: false,
			};
			Reflect.defineMetadata(BE_PROP_KEY, metadata, target, props?.keyOverride ? props.keyOverride : propertyKey);
		};
	}

	/**
	 * BESchema is a decorator factory that returns a ClassDecorator.
	 * The returned decorator will modify the toJSON method of the class prototype.
	 * The new toJSON method will iterate over the properties of the class instance,
	 * retrieve the metadata attached by BEProp or BEOptional decorators, and prepare a JSON object.
	 * If a property is marked as required but its value is undefined, an error will be thrown.
	 *
	 * @returns {ClassDecorator} - A decorator function that can be used to decorate a class.
	 *
	 * @example
	 * @BESchema()
	 * class MyClass {
	 *   @BEProp({ keyOverride: 'myProp', transform: value => `Hello, ${value}!` })
	 *   prop: string;
	 * }
	 *
	 * const myInstance = new MyClass();
	 * myInstance.prop = 'World';
	 * console.log(JSON.stringif y(myInstance)); // Outputs: { "myProp": "Hello, World!" }
	 */
	export function BESchema(): ClassDecorator {
		return target => {
			target.prototype.toJSON = function () {
				const properties: string[] = Object.getOwnPropertyNames(this);
				const json: any = {};
				let missingRequiredProps: string[] = [];
				properties.forEach(property => {
					const metadata: IBEPropMetadata = Reflect.getMetadata(BE_PROP_KEY, this, property);
					if (metadata) {
						const key: string = metadata.keyOverride || property;
						json[key] = prepareValue(this[property], metadata);
					}
				});
				missingRequiredProps = missingRequiredProps.filter(Boolean);
				if (missingRequiredProps.length) {
					const className: string = target.name;
					throw new Error(`Missing required properties in ${className}: ${missingRequiredProps.join(', ')}`);
				}
				return json;

				function prepareValue(value: any, metadata: IBEPropMetadata): any {
					let resValue: any = value;
					if (isSignal(value) || typeof value === 'function') resValue = value();
					if (metadata.transform) resValue = metadata.transform(resValue);
					if (resValue === undefined && metadata.required) missingRequiredProps.push(metadata.keyOverride);
					return resValue;
				}
			};
		};
	}
}
